import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import Logo from '../common/Logo';
import { FiHome, FiMap, FiAward, FiUser, FiPlayCircle } from 'react-icons/fi';
import { userData } from '../data/mockData';

const navItems = [
  { path: '/', icon: FiHome, label: 'Command' },
  { path: '/map', icon: FiMap, label: 'Skill Map' },
  { path: '/leaderboard', icon: FiAward, label: 'Ranks' },
  { path: '/profile', icon: FiUser, label: 'Profile' },
  { path: '/demo', icon: FiPlayCircle, label: 'Demo' },
];

export default function Layout() {
  const location = useLocation();

  return (
    <div className="flex h-screen bg-navy-950 text-white font-sans overflow-hidden selection:bg-quest-500 selection:text-navy-950">
      {/* Grid Pattern Background */}
      <div className="absolute inset-0 bg-grid-pattern bg-[length:40px_40px] opacity-10 pointer-events-none" />
      
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-navy-900 border-r border-navy-800 z-10 relative">
        <div className="p-8 pb-10 flex items-center gap-3">
          <Logo className="w-10 h-10 text-quest-500" color="currentColor" />
          <span className="text-xl font-black tracking-tighter uppercase text-white">SkillQuest</span>
        </div>

        <nav className="flex-1 px-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 group ${
                  isActive 
                    ? 'bg-navy-800 text-quest-500 border border-navy-700 shadow-[inset_0_0_20px_rgba(0,229,115,0.05)]' 
                    : 'text-gray-500 hover:text-white hover:bg-navy-800/50'
                }`
              }
            >
              <SafeIcon icon={item.icon} className={`text-lg transition-transform duration-300 group-hover:scale-110`} />
              <span className="font-bold tracking-tight">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Mini Profile */}
        <div className="p-6 border-t border-navy-800 bg-navy-900/50">
          <div className="flex items-center gap-3">
            <div className="relative">
              <img src={userData.avatar} alt="Avatar" className="w-10 h-10 rounded-full border-2 border-navy-700 object-cover" />
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-quest-500 rounded-full border-2 border-navy-900" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{userData.name}</div>
              <div className="text-[10px] font-mono text-quest-500 uppercase tracking-widest">
                LVL {userData.level} // {userData.xp} XP
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col relative z-10 overflow-hidden">
        {/* Topbar (Mobile Header) */}
        <header className="md:hidden flex items-center justify-between p-4 bg-navy-900 border-b border-navy-800">
          <div className="flex items-center gap-2">
            <Logo className="w-8 h-8 text-quest-500" color="currentColor" />
            <span className="font-black tracking-tighter uppercase text-white">SkillQuest</span>
          </div>
          <img src={userData.avatar} alt="Avatar" className="w-8 h-8 rounded-full border border-navy-700 object-cover" />
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-10 pb-28 md:pb-10">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="max-w-6xl mx-auto h-full"
          >
            <Outlet />
          </motion.div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-navy-900/90 backdrop-blur-md border-t border-navy-800 flex justify-around p-2 z-50 pb-safe">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${
                isActive ? 'text-quest-500 scale-110' : 'text-gray-600'
              }`
            }
          >
            <SafeIcon icon={item.icon} className="text-xl" />
            <span className="text-[10px] font-bold uppercase tracking-tighter">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}